import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

const CityQrModal = ({ isOpen, onClose, primaryColor = '#00595b', secondaryColor = '#ff5874' }) => {
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const [selectedCity, setSelectedCity] = useState('');
    const [isScanning, setIsScanning] = useState(false);
    const [scanResult, setScanResult] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const html5QrCodeRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            const userCity = (user?.city || '').trim();
            if (userCity) {
                if (userCity.toLowerCase() === 'lima') {
                    setSelectedCity('Lima');
                    setIsScanning(true);
                } else {
                    setSelectedCity(userCity);
                    setError(`La confirmación de ingreso presencial aplica únicamente para Lima. Tu ciudad registrada es ${userCity}.`);
                }
            }
        } else {
            setSelectedCity('');
            setIsScanning(false);
            setScanResult(null);
            setError(null);
        }
    }, [isOpen, user]);

    const handleScanSuccess = useCallback(async (qrData) => {
        setIsLoading(true);
        setError(null);
        try {
            let parsedId = qrData;
            try {
                const json = JSON.parse(qrData);
                if (json.id || json.attendance) parsedId = json.id || qrData;
            } catch (e) {}

            const recordType = user ? 'asistencia_en_el_evento' : 'asistire_al_evento';

            const res = await api.post('/attendance/self-confirm', {
                city: selectedCity,
                method: 'qr',
                record_type: recordType,
                qr_data: parsedId
            });

            if (res.data?.user && user) {
                updateUser({ ...user, ...res.data.user });
            }

            setScanResult({
                message: `¡Asistencia presencial registrada con éxito para ${selectedCity}!`
            });

            setTimeout(() => {
                onClose();
            }, 1800);
        } catch (err) {
            console.error("Scan error", err);
            setError('No se pudo verificar el código QR. Intente nuevamente.');
        } finally {
            setIsLoading(false);
        }
    }, [onClose, selectedCity, user, updateUser]);

    const startCamera = useCallback(() => {
        setError(null);
        if (!selectedCity) return;

        const timer = setTimeout(() => {
            try {
                if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
                    html5QrCodeRef.current.stop().catch(() => {});
                }

                const qrScanner = new Html5Qrcode('city-qr-reader');
                html5QrCodeRef.current = qrScanner;

                const config = { fps: 10, qrbox: { width: 220, height: 220 } };

                qrScanner.start(
                    { facingMode: 'environment' },
                    config,
                    (decodedText) => {
                        qrScanner.stop().then(() => {
                            setIsScanning(false);
                            handleScanSuccess(decodedText);
                        }).catch(console.error);
                    },
                    () => {}
                ).catch(err => {
                    console.warn("Camera environment error, attempting fallback", err);
                    qrScanner.start(
                        { facingMode: 'user' },
                        config,
                        (decodedText) => {
                            qrScanner.stop().then(() => {
                                setIsScanning(false);
                                handleScanSuccess(decodedText);
                            }).catch(console.error);
                        },
                        () => {}
                    ).catch(e => {
                        console.error("Camera access error", e);
                        setError('Por favor permite el acceso a la cámara cuando el navegador te lo solicite.');
                    });
                });
            } catch (e) {
                console.error("Scanner init error", e);
            }
        }, 150);

        return () => clearTimeout(timer);
    }, [selectedCity, handleScanSuccess]);

    useEffect(() => {
        if (isOpen && isScanning && selectedCity) {
            const cleanup = startCamera();
            return () => {
                if (cleanup) cleanup();
                if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
                    html5QrCodeRef.current.stop().catch(() => {});
                }
            };
        }
    }, [isOpen, isScanning, selectedCity, startCamera]);

    const handleCitySelect = (city) => {
        setSelectedCity(city);
        const isLima = city.toLowerCase() === 'lima';

        if (isLima) {
            setIsScanning(true);
        } else {
            const searchString = window.location.search;
            const hasParams = searchString && searchString.length > 1;

            onClose();
            if (hasParams) {
                const params = new URLSearchParams(searchString);
                if (!params.has('city') && !params.has('ciudad')) {
                    params.set('city', city);
                }
                navigate(`/confirm-attendance?${params.toString()}`);
            } else {
                navigate(`/confirm-attendance?city=${encodeURIComponent(city)}`);
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 animate-fadeIn">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>

            <div className="relative w-full max-w-md bg-white rounded-3xl p-6 sm:p-8 shadow-2xl z-10 text-center animate-scaleUp">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-5 text-slate-400 hover:text-black text-xl font-bold"
                >
                    ✕
                </button>

                <h2 style={{ color: primaryColor }} className="text-xl sm:text-2xl font-black capitalize tracking-wider mb-2">
                    Confirmar Asistencia
                </h2>
                <p className="text-xs text-slate-500 capitalize tracking-widest mb-6">
                    {!selectedCity ? 'Paso 1: Selecciona tu ciudad' : `Paso 2: Escanea el código QR en ${selectedCity}`}
                </p>

                {!selectedCity && (
                    <div className="space-y-3 my-4">
                        <label className="block text-xs font-bold capitalize tracking-wider text-slate-700 text-left pl-1">
                            Selecciona una ciudad:
                        </label>
                        <select
                            onChange={(e) => e.target.value && handleCitySelect(e.target.value)}
                            defaultValue=""
                            className="w-full bg-slate-50 border-2 border-slate-200 focus:border-[#ff5874] p-4 rounded-2xl font-black text-slate-800 capitalize tracking-wider text-sm outline-none cursor-pointer"
                        >
                            <option value="" disabled>-- Seleccionar Ciudad --</option>
                            <option value="Arequipa">Arequipa</option>
                            <option value="Piura">Piura</option>
                            <option value="Lima">Lima</option>
                        </select>
                    </div>
                )}

                {selectedCity && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between bg-slate-100 p-3 rounded-xl">
                            <span className="text-xs font-bold uppercase text-slate-700">Ciudad: <strong>{selectedCity}</strong></span>
                            <button
                                onClick={() => { setSelectedCity(''); setIsScanning(false); }}
                                className="text-[10px] font-bold text-blue-600 uppercase underline"
                            >
                                Cambiar
                            </button>
                        </div>

                        {scanResult ? (
                            <div className="bg-emerald-50 text-emerald-700 border border-emerald-200 p-4 rounded-2xl font-bold text-sm">
                                {scanResult.message}
                            </div>
                        ) : isLoading ? (
                            <div className="py-10 flex flex-col items-center">
                                <div className="w-10 h-10 border-4 border-slate-200 border-t-[#00595b] rounded-full animate-spin"></div>
                                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mt-4">Procesando escaneo...</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {/* Explicit Permission Request Banner */}
                                <div className="bg-slate-50 border border-slate-200 text-slate-700 p-3 rounded-xl text-xs font-bold flex items-center justify-between text-left shadow-sm">
                                    <div className="flex items-center gap-2">
                                        <span className="text-base">📷</span>
                                        <span>Autoriza el acceso a la cámara para iniciar la lectura QR.</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={startCamera}
                                        style={{ backgroundColor: secondaryColor }}
                                        className="text-white text-[10px] font-black uppercase px-3 py-1.5 rounded-lg shrink-0 shadow hover:opacity-90"
                                    >
                                        Activar
                                    </button>
                                </div>

                                <div id="city-qr-reader" className="w-full rounded-2xl overflow-hidden border-2 border-slate-200 min-h-[250px] bg-slate-900"></div>
                                
                                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-2">
                                    Enfoca el código QR de asistencia con tu cámara
                                </p>
                            </div>
                        )}

                        {error && (
                            <div className="bg-red-50 text-red-600 p-3 rounded-xl text-xs font-bold border border-red-200">
                                {error}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CityQrModal;
